import { useRollbarPerson } from "@rollbar/react";
import { data, Outlet, useLoaderData, useSubmit } from "react-router";

import NavigationMenu from "@/components/NavigationMenu";
import { FeaturesContext, UserContext } from "@/contexts";
import { FeatureFlags, TSimulation, TTrackingCampaign, User } from "@/types";

export async function loader() {
  try {
    // when not login, we get redirected to the logged in page, we still get a 200 instead of 302, so we need to check the content type
    let user;

    const userResponse = await fetch(`/api/client/users/me/`);

    if (userResponse.status !== 200) {
      console.error("Failed to load the user data");
    }

    if (
      userResponse.headers.get("Content-Type")?.includes("application/json")
    ) {
      const { data: userData } = (await userResponse.json()) as {
        data: User;
      };
      user = userData;
    }

    let sortedTrackingCampaignData = [] as TTrackingCampaign[];
    let hasSimulations = false;

    if (user) {
      const trackingCampaignResponse = await fetch(
        `/api/client/tracking_campaigns`
      );
      if (trackingCampaignResponse.status !== 200) {
        throw data("Failed to load the tracking campaign data.", {
          status: trackingCampaignResponse.status,
        });
      }

      const { data: trackingCampaignData } =
        (await trackingCampaignResponse.json()) as {
          data: TTrackingCampaign[];
        };

      sortedTrackingCampaignData = trackingCampaignData.sort(
        (a, b) =>
          new Date(a.period_start).getTime() -
          new Date(b.period_start).getTime()
      );

      const simulationsResponse = await fetch(`/api/client/simulations`);
      if (simulationsResponse.status === 200) {
        const { data: simulations } = (await simulationsResponse.json()) as {
          data: TSimulation[];
        };
        hasSimulations = simulations.length > 0;
      }
    }

    const featureFlagsResponse = await fetch(`/api/client/features/`);

    if (featureFlagsResponse.status !== 200) {
      throw data("Failed to load the user feature flags", {
        status: featureFlagsResponse.status,
      });
    }

    const { data: featureFlags } = (await featureFlagsResponse.json()) as {
      data: FeatureFlags;
    };

    return {
      trackingCampaigns: sortedTrackingCampaignData,
      user,
      featureFlags,
      hasSimulations,
    };
  } catch (error) {
    console.error("Failed to load the user data", error);
    return {
      trackingCampaigns: [],
      user: undefined,
      featureFlags: undefined,
      hasSimulations: false,
    };
  }
}

function App() {
  const { trackingCampaigns, user, featureFlags, hasSimulations } =
    useLoaderData<typeof loader>();
  const submit = useSubmit();

  useRollbarPerson({ id: user?.id });

  const handleLogOutClick = () => {
    submit(null, { method: "DELETE" });
  };

  return (
    <UserContext value={{ user }}>
      <FeaturesContext value={{ featureFlags }}>
        <div className="fixed top-0 left-0 z-50 flex h-dvh">
          {user ? (
            <div className="relative h-full">
              <NavigationMenu
                trackingCampaigns={trackingCampaigns}
                handleLogOutClick={handleLogOutClick}
                hasSimulations={hasSimulations}
              />
            </div>
          ) : null}
          <div className="flex-grow overflow-y-auto">
            <Outlet />
          </div>
        </div>
      </FeaturesContext>
    </UserContext>
  );
}

export default App;
